"use client";

import { usePreferences } from "@/app/hooks/usePreferences";
import { trackList } from "@/lib/analytics";
import { createTimestamp } from "@/lib/sync/operations";
import type { AppState, IngredientList, Operation } from "@/lib/types";
import { generateId } from "@/lib/utils";

interface UseListManagerProps {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  queueOperation: (operation: Operation) => void;
}

export function useListManager({ state, setState, queueOperation }: UseListManagerProps) {
  const { updatePreferences } = usePreferences();

  const addList = (name: string, color: string) => {
    const newList: IngredientList = {
      id: generateId(),
      name,
      color,
      ingredients: [],
    };

    // Update local state
    setState((prev) => ({
      lists: [...prev.lists, newList],
      activeListId: newList.id,
    }));

    // Update activeListId preference (will sync to server if authenticated)
    updatePreferences({ activeListId: newList.id });

    trackList("created");

    queueOperation({
      type: "ADD_LIST",
      list: newList,
      timestamp: createTimestamp(),
    });
  };

  const selectList = (listId: string) => {
    // Find the list being selected
    const targetList = state.lists.find((list) => list.id === listId);

    // Prevent selecting disabled lists
    if (targetList?.isDisabled) {
      console.warn(`❌ Attempted to select disabled list ${listId}`);
      return;
    }

    // Update local state
    setState((prev) => ({ ...prev, activeListId: listId }));

    // Update activeListId preference (will sync to server if authenticated)
    updatePreferences({ activeListId: listId });
  };

  const updateList = (listId: string, name: string, color: string) => {
    const _oldList = state.lists.find((list) => list.id === listId);

    // Update local state
    setState((prev) => ({
      ...prev,
      lists: prev.lists.map((list) => (list.id === listId ? { ...list, name, color } : list)),
    }));

    // Queue operation
    queueOperation({
      type: "UPDATE_LIST",
      id: listId,
      changes: { name, color },
      timestamp: createTimestamp(),
    });
  };

  const deleteList = (listId: string) => {
    // Update local state
    setState((prev) => {
      const newLists = prev.lists.filter((list) => list.id !== listId);

      // If deleting the active list, find first non-disabled list
      let newActiveId = prev.activeListId;
      if (prev.activeListId === listId) {
        const firstAvailable = newLists.find((list) => !list.isDisabled);
        newActiveId = firstAvailable ? firstAvailable.id : null;
      }

      return {
        lists: newLists,
        activeListId: newActiveId,
      };
    });

    // If we're deleting the active list, update the preference with the new active list
    if (state.activeListId === listId) {
      const newLists = state.lists.filter((list) => list.id !== listId);
      const firstAvailable = newLists.find((list) => !list.isDisabled);
      const newActiveId = firstAvailable ? firstAvailable.id : null;
      updatePreferences({ activeListId: newActiveId });
    }

    trackList("deleted");

    queueOperation({
      type: "DELETE_LIST",
      id: listId,
      timestamp: createTimestamp(),
    });
  };

  return {
    addList,
    selectList,
    updateList,
    deleteList,
  };
}
